/*
  Panchobot — asistente del catálogo de Degrow.

  No hay modelo de lenguaje ni servidor: todo corre en el navegador del visitante contra un
  índice que se genera con scripts/generar-panchobot.mjs. Eso lo hace gratis de operar y sin
  terceros que vean las conversaciones, a cambio de que solo sepa lo que está escrito.

  El orden del pipeline no es casual, cada paso existe por una razón:
    1. límites        — salud y legal se cortan ANTES de buscar, no vaya a ser que el catálogo
                        tenga algo que parezca una respuesta
    2. temas ajenos   — futbol, código, recetas: no es lo que hace la tienda
    3. respuestas     — las dudas de cultivo se contestan con texto escrito a mano, que dice un
                        número concreto en vez de mostrar un producto y esperar que se entienda
    4. catálogo       — recién acá se busca producto
    5. derivar        — si nada calzó, a WhatsApp, sin inventar

  Los PRECIOS nunca salen del índice: se piden a /products/<handle>.js al momento de mostrar.
  Un índice se queda viejo y cotizar mal es peor que no responder.
*/
(() => {
  'use strict';

  const $ = (sel, raiz = document) => raiz.querySelector(sel);
  const limpiar = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const VACIAS = new Set('de la el los las un una y o para con sin por en al del que es son mas muy su sus lo se te me mi tu hay tiene tienen quiero busco necesito me sirve cual cuales como cuanto cuanta donde'.split(' '));
  // Un token con letras Y dígitos es un nombre de modelo (f720w, k30, ph51). Se indexa
  // también en variantes — "f720w" además como "720w", "f720" y "720" — porque el cliente
  // escribe el modelo como lo recuerda, no como lo escribió el proveedor en el título.
  // Singular de un plural español, a lo bruto pero PAREJO: se aplica igual al catálogo y a la
  // consulta, así que basta con que las dos caigan en la misma forma aunque no sea la correcta
  // ("luces" → "luc" no es español, pero calza consigo mismo y no rompe nada).
  //
  // Sin esto, el cliente escribía "kits" y el catálogo decía "kit", y no se encontraban: había
  // 61 kits y el bot contestó que no manejaba el tema. Peor todavía con "carpas", que no calza
  // con NINGÚN título (todos dicen "carpa"). Se venía tapando con sinónimos escritos a mano
  // para cada palabra, que es un parche que solo cubre lo que alguien se acordó de anotar.
  const singular = t => (t.length > 4 && t.endsWith('es') ? t.slice(0, -2)
    : t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : null);

  const tokenizar = s => {
    const out = [];
    for (const t of limpiar(s).split(/[^a-z0-9]+/)) {
      if (t.length < 2 || VACIAS.has(t)) continue;
      out.push(t);
      const uno = singular(t);
      if (uno && !VACIAS.has(uno)) out.push(uno);
      if (/\d/.test(t) && /[a-z]/.test(t)) {
        const nucleo = (t.match(/\d{2,}/) || [])[0];
        for (const v of new Set([t.replace(/^[a-z]+/, ''), t.replace(/[a-z]+$/, ''), nucleo])) {
          if (v && v !== t && v.length > 1) out.push(v);
        }
      }
    }
    return out;
  };
  const clp = n => '$' + Number(n || 0).toLocaleString('es-CL');

  let D = null;              // datos del índice
  let docs = null;           // documentos tokenizados
  let df = null, N = 0, largoMedio = 0;
  let cargando = null;

  // ── Índice ─────────────────────────────────────────────────────────────────────
  async function cargar(url) {
    if (D) return;
    if (cargando) return cargando;
    cargando = (async () => {
      D = await (await fetch(url)).json();
      D.ajenosRe = new RegExp(D.ajenos, 'i');
      // Cada sinónimo apunta a su palabra canónica, para expandir la consulta en un paso.
      D.mapaSinonimos = {};
      for (const [base, lista] of Object.entries(D.sinonimos)) {
        for (const s of lista) D.mapaSinonimos[limpiar(s)] = base;
        D.mapaSinonimos[base] = base;
      }
      D.porHandle = new Map(D.productos.map(p => [p.h, p]));
      // DOS campos con peso distinto, no una sola bolsa de palabras:
      //
      //   `tokens` — título (×3), marca y tags. Es lo que el producto ES.
      //   `desc`   — la descripción completa. Es lo que el producto DICE.
      //
      // Mezclarlos en una bolsa se probó y salió mal: al indexar las descripciones enteras,
      // "limpiar" aparecía en decenas de fichas de bongs ("fácil de limpiar"), la palabra
      // dejó de distinguir y la consulta "algo para limpiar bongs" devolvía bongs en vez del
      // limpiador. Separados, un match en el título vale casi el triple que uno en la ficha.
      //
      // Los sinónimos se expanden SOLO en el título: expandirlos también en la descripción
      // volvía a contaminar — cada bong que menciona "limpiar" pasaba a contar como limpiador.
      docs = D.productos.map(p => ({
        p,
        tokens: expandir(tokenizar(`${p.t} ${p.t} ${p.t} ${p.m} ${p.g}`)),
        desc: tokenizar(p.w || ''),
      }));
      N = docs.length;
      largoMedio = docs.reduce((a, d) => a + d.tokens.length, 0) / N;
      df = new Map();
      for (const d of docs) {
        d.tf = new Map();
        for (const t of d.tokens) d.tf.set(t, (d.tf.get(t) || 0) + 1);
        d.tfDesc = new Map();
        for (const t of d.desc) d.tfDesc.set(t, (d.tfDesc.get(t) || 0) + 1);
        // La frecuencia por documento se cuenta sobre los DOS campos: una palabra que sale en
        // media tienda tiene que valer poco, esté en el título o en la ficha.
        for (const t of new Set([...d.tf.keys(), ...d.tfDesc.keys()])) df.set(t, (df.get(t) || 0) + 1);
      }
      // Tokens de modelo del catálogo (letras+dígitos: "f720w", "k30"). Sirven para saber si
      // una pregunta nombra un producto concreto ANTES de decidir con qué se le responde.
      D.modelosCatalogo = new Set();
      for (const t of df.keys()) if (/\d/.test(t) && /[a-z]/.test(t)) D.modelosCatalogo.add(t);
    })();
    return cargando;
  }

  // ── Entender la pregunta ───────────────────────────────────────────────────────

  // "Qué LED me sirve para una carpa de 1x1" pide LED; la carpa es contexto. En español lo
  // pedido va ANTES del "para", así que se parte ahí y se busca la categoría a la izquierda.
  // Sin esto el buscador devolvía carpas, que fue el peor error de la prueba inicial.
  function categoriaPedida(texto) {
    const t = limpiar(texto);
    const izquierda = t.split(/\bpara\b|\bpa'\b/)[0];
    const buscarEn = zona => {
      for (const c of D.categorias) {
        for (const palabra of c.pide) if (new RegExp(`\\b${palabra}`, 'i').test(zona)) return c.id;
      }
      return null;
    };
    return buscarEn(izquierda) || buscarEn(t);
  }

  // Medidas: "de 120", "1x1", "60x60", "20 litros". Se normaliza metros a centímetros.
  function medidasPedidas(texto) {
    const t = limpiar(texto);
    const nums = new Set();
    for (const m of t.matchAll(/(\d{2,3})\s*[x×]\s*(\d{2,3})/g)) { nums.add(+m[1]); nums.add(+m[2]); }
    for (const m of t.matchAll(/\b(\d{1,2})\s*[x×]\s*(\d{1,2})\b/g)) {
      if (+m[1] <= 4 && +m[2] <= 4) { nums.add(+m[1] * 100); nums.add(+m[2] * 100); }
    }
    for (const m of t.matchAll(/\bde\s+(\d{2,3})\b/g)) nums.add(+m[1]);
    for (const m of t.matchAll(/\b(\d{1,4})\s*(?:l|lt|litros)\b/g)) nums.add(+m[1]);
    return [...nums];
  }

  // ── Presupuesto ────────────────────────────────────────────────────────────────
  // "Tengo 100 mil máximo" y recibir uno de $356.990 es no escuchar. El precio del índice
  // sirve de referencia para filtrar; el que se muestra en la tarjeta sigue siendo el de
  // /products/<handle>.js, en vivo.
  //
  // Se cubren las formas en que un chileno dice plata: "100 mil", "100k", "100 lucas",
  // "$100.000", "100000". Un número pelado bajo 1.000 se interpreta como miles ("tengo 80")
  // porque nadie compra un vaporizador con 80 pesos.
  // Devuelve { monto, asumido } o null. `asumido` marca que la cifra venía pelada ("menos de
  // 150") y se interpretó como miles: nadie compra un vaporizador con 150 pesos. El bot lo
  // dice en voz alta en vez de adivinar en silencio.
  //
  // La palabra clave se exige SOLO para cifras peladas. "150 mil" no la necesita: en una
  // conversación de compra eso es plata y punto — pedirla hacía que "por 150 me refiero a 150
  // mil" no se entendiera, y el bot siguió ofreciendo vaporizadores de $356.990.
  const FRASE_PLATA = '(?:menos de|maximo de|maximo|max|hasta|presupuesto de|presupuesto|tengo|ando con|dispongo de|gastar|no mas de|bajo los|bajo|por debajo de)';
  function presupuestoDe(texto) {
    const t = limpiar(texto);
    const num = s => Math.round(parseFloat(String(s).replace(/[.,]/g, '')));

    // 1. Cifra con forma de plata: "150 mil", "150k", "150 lucas".
    let m = t.match(/(?<![a-z0-9])(\d[\d.,]*)\s*(mil|k|lucas?)(?![a-z0-9])/);
    if (m) return { monto: num(m[1]) * 1000, asumido: false };
    // 2. Monto escrito completo: "$150.000" o "150000".
    m = t.match(/\$\s*(\d[\d.]{3,})/) || t.match(/(?<![a-z0-9])(\d{5,7})(?![a-z0-9])/);
    if (m) return { monto: num(m[1]), asumido: false };
    // 3. Cifra pelada de 2 o 3 dígitos, pero SOLO pegada a una frase de plata. Sin esa
    //    exigencia, "el sn 630 pro" se leía como $630.000 y filtraba justo el equipo pedido.
    //    Se descartan las que llevan unidad detrás (720w, 60cm, 20 litros): son medidas.
    m = t.match(new RegExp(`${FRASE_PLATA}\\s*\\$?\\s*(\\d{2,3})(?![.\\d])(?!\\s*(?:w|watt|watts|cm|mm|ml|lt|litros|l\\b|x))`));
    if (m) return { monto: num(m[1]) * 1000, asumido: true };
    return null;
  }

  // Contexto = SOLO lo que dijo el cliente. Las respuestas del bot no sirven para inferir:
  // su "Esto es lo que tengo: CARPA 60x60..." hacía creer que la conversación era de carpas,
  // y su propio "tengo" activaba el detector de presupuesto sobre precios que él mismo pintó.
  const contextoDelCliente = historial =>
    historial.filter(m => m.rol === 'user').slice(-3).map(m => m.texto).join(' ');

  const pideBarato = t => /mas barat|mas econom|mas bajo|menor precio|barato/.test(limpiar(t));
  const pideCaro = t => /mas car|mejor que teng|top de|premium|mas potente/.test(limpiar(t));
  // "¿solo tienes esos?", "muéstrame más", "hay otros": el cliente quiere ver el resto.
  const pideMas = t => /\b(mas|otros?|otras?|todos?|todas?|resto|solo (tienes|tenis|hay|esos|esas))\b/.test(limpiar(t))
    && !/mas barat|mas econom|mas car|mas potente|que mas (tengo|debo|necesito)/.test(limpiar(t));

  function expandir(tokens) {
    const fuera = new Set(tokens);
    for (const t of tokens) {
      const base = D.mapaSinonimos[t];
      if (base) fuera.add(base);
    }
    return [...fuera];
  }

  // ── Buscar en el catálogo ──────────────────────────────────────────────────────
  // `contexto` es lo que se dijo antes en la conversación. Es indispensable desde que el bot
  // hace preguntas: si Pancho pregunta "¿de qué tamaño es tu espacio?" y el cliente contesta
  // "60x60", esa respuesta sola no dice de qué se está hablando y el buscador devolvía
  // carpas. La categoría se hereda de lo conversado; la medida la manda el mensaje nuevo,
  // que es justo lo que el cliente acaba de responder.
  function buscar(consulta, tope = 4, contexto = '') {
    // La categoría se hereda del contexto SOLO si el mensaje nuevo no trae tema propio. Un
    // "60x60" suelto sí necesita heredar; "tienes kits de cultivo?" no, y heredaba igual:
    // venía de una conversación sobre vaporizadores, así que los vapos subían +12 y los kits
    // caían ×0.15. El cliente preguntaba por kits y recibía cámaras Puffco.
    //
    // El criterio es el largo: tres palabras o menos es una respuesta, no una pregunta nueva.
    const propias = tokenizar(consulta);
    const cat = categoriaPedida(consulta)
      || (contexto && propias.length <= 3 ? categoriaPedida(contexto) : null);
    const medidas = medidasPedidas(consulta);
    // El presupuesto se hereda de lo conversado: se dice UNA vez ("tengo 100 mil máximo") y
    // vale para todo lo que venga después, aunque la pregunta siguiente no lo repita.
    const pres = presupuestoDe(consulta) || (contexto ? presupuestoDe(contexto) : null);
    const tope$ = pres ? pres.monto : null;
    const q = expandir(tokenizar(consulta));
    const k1 = 1.5, b = 0.75;

    // ¿El cliente nombró una marca? Si la pidió, esa manda y el empujón de la preferida no
    // debe pasarle por encima: pedir Storz & Bickel y recibir Puffco es no escuchar.
    //
    // Se busca SOLO en el mensaje actual, nunca en el contexto: una marca nombrada tres
    // preguntas atrás secuestraba la búsqueda nueva — al preguntar por vaporizadores después
    // de hablar de un LED Nanolux, salían LEDs. El contexto sirve para heredar la CATEGORÍA
    // cuando la respuesta es corta, no para arrastrar lo que ya se cerró.
    const marcaPedida = (D.marcas || []).find(m => calza(limpiar(consulta), m));

    // Tokens de modelo en la consulta (letras+dígitos, como "f720w"). Si uno calza en un
    // producto, ESO es lo pedido, aunque la frase nombre otra categoría: en "quiero el f720w
    // pero no sé para qué carpa me sirve", la carpa es contexto y el modelo es el pedido.
    // Sin esto, el +12 de la categoría enterraba al LED bajo ocho carpas y la IA respondía
    // que el modelo no existía — le pasó a un cliente real el día del estreno.
    const modelos = q.filter(t => /\d/.test(t) && /[a-z]/.test(t));

    // Lo que aporta la ficha vale un tercio de lo que aporta el nombre. No es cero —para eso
    // se indexó— pero un producto que se LLAMA como lo pedido siempre le gana a uno que solo
    // lo menciona de pasada.
    const PESO_DESC = 0.35;

    let res = docs.map(d => {
      let s = 0;
      for (const t of q) {
        const idf = Math.log(1 + (N - df.get(t) + 0.5) / (df.get(t) + 0.5));
        const f = d.tf.get(t);
        if (f) s += idf * (f * (k1 + 1)) / (f + k1 * (1 - b + b * d.tokens.length / largoMedio));
        const fd = d.tfDesc.get(t);
        if (fd) s += PESO_DESC * idf * (fd * (k1 + 1)) / (fd + k1 * (1 - b + b * d.desc.length / largoMedio));
      }
      const esModelo = modelos.some(t => d.tf.has(t));
      if (esModelo) s += 15;
      // La categoría pedida manda por sobre la coincidencia de texto: es la diferencia entre
      // responder "un LED" y responder "una carpa que menciona LED en la descripción".
      // Salvo que el producto calce por modelo: ahí la categoría ni suma ni castiga.
      if (cat && !esModelo) {
        s = d.p.c === cat ? s + 12 : s * 0.15;
        // Que la categoría salga del NOMBRE y no solo del tipo separa un bong de un
        // limpiavidrios de bongs. Pesa poco en relevancia y mucho al ordenar por precio.
        if (d.p.c === cat && d.p.n) s += 4;
      }
      // Una medida que calza es una señal fuerte: "carpa de 120" y el título dice 120x120.
      if (medidas.length && d.p.d.some(x => medidas.includes(x))) s += 8;
      // Y si NO calza, el equipo se hunde. Un LED trae la carpa que el fabricante le asignó:
      // ofrecer un 720W para un espacio de 60×60 no es una recomendación imprecisa, es
      // venderle al cliente el equipo equivocado. Solo aplica a productos con carpa conocida,
      // porque ahí el dato es del fabricante y no una deducción nuestra.
      else if (medidas.length && d.p.k) s *= 0.1;
      // Marca: la pedida manda; si no pidieron ninguna, la preferida de la tienda va primero.
      // El empujón compite dentro de la categoría, así que nunca saca de contexto: no va a
      // ofrecer un vaporizador cuando preguntan por un bong.
      // Multiplicador y no suma: la marca DESEMPATA entre cosas que ya calzan, nunca arrastra
      // a un producto que no tiene nada que ver. Con +6 fijo, preguntar por "kits de cultivo"
      // devolvía cámaras Puffco: el empujón de la marca preferida pesaba lo mismo que un match
      // de título, así que cualquier Puffco le ganaba al producto correcto.
      if (marcaPedida) {
        s = limpiar(d.p.m) === limpiar(marcaPedida) ? s * 1.8 : s * 0.2;
      } else if ((D.preferidas || []).some(m => limpiar(m) === limpiar(d.p.m))) {
        s *= 1.3;
      }
      if (!d.p.s) s -= 3;                       // sin stock: se muestra, pero al final
      return { p: d.p, s };
    }).filter(x => x.s > 0.5);

    // "El más barato" es una pregunta de orden, no de relevancia.
    // Al ordenar por precio se sacan primero los que ni siquiera llevan la categoría en el
    // nombre: el más barato de una categoría suele ser un accesorio, no el producto.
    if (pideBarato(consulta) || pideCaro(consulta)) {
      const propios = cat ? res.filter(x => x.p.n && x.p.c === cat) : res;
      if (propios.length) res = propios;
    }
    if (pideBarato(consulta)) res.sort((a, b) => (a.p.p || 1e9) - (b.p.p || 1e9));
    else if (pideCaro(consulta)) res.sort((a, b) => (b.p.p || 0) - (a.p.p || 0));
    else res.sort((a, b) => b.s - a.s);

    // El presupuesto se aplica al final y como FILTRO, no como puntaje: un producto sobre el
    // máximo que dijo el cliente no es "menos relevante", es una opción que no puede comprar.
    // Si no queda ninguno dentro, se devuelven igual los más baratos — para poder decirle que
    // no hay nada en ese rango en vez de quedarse mudo — y se avisa con `sobrePresupuesto`.
    let sobrePresupuesto = false;
    if (tope$) {
      const dentro = res.filter(x => x.p.p > 0 && x.p.p <= tope$);
      if (dentro.length) res = dentro;
      else if (res.length) { res = [...res].sort((a, b) => (a.p.p || 1e9) - (b.p.p || 1e9)); sobrePresupuesto = true; }
    }

    // `total` es cuántos calzan de verdad, no cuántos se muestran. Sin ese número el bot creía
    // que los 8 candidatos eran todo el catálogo y contestaba "sí, solo esos tengo" teniendo
    // decenas. Decirle que no hay a un cliente que quería comprar es el peor error posible.
    return { cat, total: res.length, presupuesto: tope$, presupuestoAsumido: !!(pres && pres.asumido), sobrePresupuesto, resultados: res.slice(0, tope) };
  }

  // ── Responder ──────────────────────────────────────────────────────────────────
  // Una clave calza como PALABRA, no como trozo. Con `includes` la clave 'ec' se activaba
  // dentro de "n·ec·esito" y "qué necesito para empezar" contestaba la tabla de EC.
  const cacheClave = new Map();
  function calza(texto, clave) {
    let re = cacheClave.get(clave);
    if (!re) {
      const c = limpiar(clave).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      re = new RegExp(`(^|[^a-z0-9])${c}([^a-z0-9]|$)`, 'i');
      cacheClave.set(clave, re);
    }
    return re.test(texto);
  }

  // Los límites duros viven en el NAVEGADOR aunque haya IA: salud, legal y temas ajenos se
  // cortan antes de gastar una llamada, y siguen cortándose igual si la API está caída o si
  // alguien intenta convencer al modelo de saltárselos — a este filtro no se le habla.
  function esIlicito(pregunta) {
    if (!D.ilegal) return false;
    const t = limpiar(pregunta);
    return D.ilegal.claves.some(c => calza(t, c));
  }

  // ¿Es una insistencia sobre lo mismo? "seguro??", "en serio", "pero quiero comprar", "dale".
  // Son mensajes cortos que no traen tema nuevo: viven pegados a lo anterior. Sin esto, un
  // "seguro?? es que quiero comprar" después de un "eso no lo vendemos" se trataba como
  // pregunta NUEVA, se buscaba en el catálogo y el bot terminaba ofreciendo sustratos dentro
  // de una conversación sobre algo que la tienda no vende.
  const esInsistencia = t =>
    limpiar(t).length < 70
    && /\b(seguro|segurisimo|en serio|de verdad|dale|porfa|por favor|pero|igual|ya po|insisto|vamos|no seai|no seas|aunque sea|de una)\b/.test(limpiar(t));

  function limiteLocal(pregunta) {
    const t = limpiar(pregunta);
    for (const l of D.limites) {
      // La tienda vende 111 semillas: si el mensaje las nombra, es una consulta legítima del
      // rubro aunque también diga "cannabis". Sin esta excepción, "tienen semillas de
      // cannabis" se respondía con un "eso no lo vendemos" siendo que sí se vende.
      if (l.tema === 'la planta' && /\bsemilla/.test(t)) continue;
      if (l.claves.some(c => calza(t, c))) return { texto: l.r };
    }
    if (D.ajenosRe.test(t)) return { texto: D.textos.fuera };
    return null;
  }

  // Respuesta escrita a mano que calza con la pregunta, o null. Gana la que más claves calce,
  // y a igualdad gana la clave más larga: "que necesito para empezar" es más específica que
  // "ec" y tiene que ganarle.
  function respuestaEscrita(pregunta) {
    const t = limpiar(pregunta);
    let mejor = null, mejorPuntaje = 0;
    for (const r of D.respuestas) {
      const calzan = r.claves.filter(c => calza(t, c));
      if (!calzan.length) continue;
      const puntaje = calzan.length * 100 + Math.max(...calzan.map(c => c.length));
      if (puntaje > mejorPuntaje) { mejorPuntaje = puntaje; mejor = r; }
    }
    return mejor ? mejor.r : null;
  }

  // ¿La pregunta nombra un modelo que existe en el catálogo? ("quiero el f720w")
  const nombraModelo = pregunta =>
    tokenizar(pregunta).some(t => /\d/.test(t) && /[a-z]/.test(t) && D.modelosCatalogo.has(t));

  // El producto que la pregunta nombra. Es el ancla de la respuesta: cuando alguien dice "voy
  // a agregar el sn 630 pro, ¿qué más?", ese equipo tiene que estar ahí — es de lo que está
  // hablando — y los complementos van detrás.
  //
  // Se apoya en `buscar` en vez de filtrar el índice a mano: el ranking ya sabe que "f720w"
  // pesa más que "720w" (es más raro), y filtrando a mano salía el primer LED del índice que
  // compartiera un número. Se exige que el resultado calce con un token de MODELO para no
  // anclar en cualquier producto que ronde la frase.
  function anclaModelo(pregunta) {
    const tk = tokenizar(pregunta).filter(t => /\d/.test(t) && (D.modelosCatalogo.has(t) || df.has(t)));
    if (!tk.length) return [];
    const top = buscar(pregunta, 3).resultados.filter(({ p }) => {
      const d = docs.find(x => x.p.h === p.h);
      return d && tk.some(t => d.tf.has(t));
    });
    return top.slice(0, 1).map(({ p }) => ({ p, s: 20 }));
  }

  // Versión local (sin IA) de los complementos: mismo criterio, sin gastar llamada.
  async function responderComplementos(pregunta) {
    const carro = await carritoActual();
    const ancla = anclaModelo(pregunta);
    // Lo que nombró la pregunta cuenta como "ya lo tiene": si dijo que va a llevar el 630, no
    // se le ofrece otro LED al lado.
    const cats = new Set([...carro.cats, ...ancla.map(({ p }) => p.c).filter(Boolean)]);
    const fuera = [...carro.items.map(i => i.h), ...ancla.map(({ p }) => p.h)];
    const faltan = faltantes(cats, fuera);
    if (!faltan.length) return null;
    const lleva = [...carro.items.map(i => i.t), ...ancla.map(({ p }) => p.t)];
    return {
      texto: lleva.length
        ? `Con ${lleva.join(' y ')} ya tienes lo principal. Para completar el cultivo te falta esto:`
        : 'Para armar el cultivo completo necesitas esto:',
      productos: [...ancla, ...faltan].slice(0, 7),
    };
  }

  function responder(pregunta, contexto = '') {
    const t = limpiar(pregunta);

    const corte = limiteLocal(pregunta);
    if (corte) return corte;

    // "¿solo tienes esos?" no es una pregunta nueva: es pedir el resto de la lista anterior.
    if (pideMas(pregunta) && ultima.consulta) {
      const mas = siguientes();
      if (mas) return mas;
    }

    // Si la pregunta nombra un modelo del catálogo, el producto ES la respuesta: una escrita
    // a mano que calce de rebote ("...no sé para qué carpa me sirve") es contexto, no el
    // pedido. Un cliente real pidió el f720w y recibió la tabla de tallas de carpa sin el LED.
    if (!nombraModelo(pregunta)) {
      let mejor = null, mejorPuntaje = 0;
      for (const r of D.respuestas) {
        const calzan = r.claves.filter(c => calza(t, c));
        if (!calzan.length) continue;
        const puntaje = calzan.length * 100 + Math.max(...calzan.map(c => c.length));
        if (puntaje > mejorPuntaje) { mejorPuntaje = puntaje; mejor = r; }
      }
      if (mejor) {
        const extra = mejor.busca ? buscar(mejor.busca, 3).resultados : [];
        return { texto: mejor.r, productos: extra, pie: extra.length ? 'Para eso te sirve:' : '' };
      }
    }

    const { resultados, total, presupuesto, sobrePresupuesto } = buscar(pregunta, 4, contexto);
    if (resultados.length) {
      // Nada dentro del presupuesto: se dice derecho y se muestran los más baratos, en vez de
      // ofrecer algo cuatro veces más caro como si nada.
      if (sobrePresupuesto) {
        return {
          texto: `Con ${clp(presupuesto)} no me queda nada en eso. Lo más barato que tengo parte en ${clp(resultados[0].p.p)}:`,
          productos: resultados.slice(0, 3),
          derivar: true,
        };
      }
      // Si preguntan por el espacio que cubre un equipo y el mejor resultado trae la carpa
      // recomendada del fabricante (metafield en Shopify), la respuesta es ese dato — no la
      // regla general ni una derivación a WhatsApp por algo que la ficha ya sabe.
      const top = resultados[0].p;
      if (top.k && /carpa|espacio|cubr|cobertura|tamano|medida|superficie/.test(t)) {
        return { texto: `El ${top.t} está recomendado para una carpa de ${top.k}.`, productos: resultados.slice(0, 3) };
      }
      return {
        texto: 'Esto es lo que tengo:',
        productos: resultados,
        tope: presupuesto || null,
        // Decir cuántos hay evita que el cliente crea que eso es todo el catálogo.
        pieFinal: total > resultados.length ? `Tengo ${total} en total. Dime qué buscas y te acoto.` : '',
      };
    }
    return { texto: D.textos.noSe, derivar: true };
  }

  // ── Modo IA ────────────────────────────────────────────────────────────────────
  // Si el widget trae data-ia con la URL del endpoint, las preguntas que pasan los límites
  // locales van a Claude (vía nuestro servidor en Vercel: la key jamás toca el navegador).
  // El buscador local no desaparece: elige los CANDIDATOS que el modelo puede recomendar
  // —así no puede inventar productos— y queda de respaldo completo si el endpoint falla.
  // ── Ver más ────────────────────────────────────────────────────────────────────
  // Lo último que se buscó y lo que ya se mostró, para poder seguir la lista en vez de
  // repetir los mismos tres productos o, peor, afirmar que no hay más.
  const ultima = { consulta: '', mostrados: new Set() };
  function recordarMostrados(consulta, productos) {
    if (consulta) ultima.consulta = consulta;
    for (const { p } of productos || []) ultima.mostrados.add(p.h);
  }
  // Los siguientes de la última búsqueda, sin los ya vistos. Devuelve null si no hay más.
  function siguientes(tope = 4) {
    if (!ultima.consulta) return null;
    const { total, resultados } = buscar(ultima.consulta, 60);
    const nuevos = resultados.filter(({ p }) => !ultima.mostrados.has(p.h)).slice(0, tope);
    if (!nuevos.length) return null;
    const quedan = total - ultima.mostrados.size - nuevos.length;
    return {
      texto: quedan > 0
        ? `Sí, tengo más. Estos también:`
        : 'Sí, tengo más. Estos son los que faltaban:',
      productos: nuevos,
      pieFinal: quedan > 0 ? `Y todavía quedan ${quedan} más. Dime qué buscas y te acoto.` : '',
    };
  }

  async function responderIA(urlIa, historial, pregunta) {
    // Los CANDIDATOS son lo único que el modelo puede recomendar, así que la corrección va
    // acá: si el cliente pide complementos, se le mandan los productos de las categorías que
    // le FALTAN, no ocho más de lo mismo que ya lleva.
    const carro = await carritoActual();
    const enCarro = carro.items.map(i => i.h);
    // Lo conversado sirve para entender respuestas cortas: si Pancho preguntó por el tamaño y
    // el cliente contesta "60x60", la categoría se hereda de ahí.
    const contexto = contextoDelCliente(historial);
    let base, total = 0, presupuesto = null, presupuestoAsumido = false, sobrePresupuesto = false;
    if (pideComplementos(pregunta)) {
      const ancla = anclaModelo(pregunta);
      const cats = new Set([...carro.cats, ...ancla.map(({ p }) => p.c).filter(Boolean)]);
      const fuera = [...enCarro, ...ancla.map(({ p }) => p.h)];
      base = [...ancla, ...faltantes(cats, fuera)].slice(0, 8);
      total = base.length;
    } else if (pideMas(pregunta) && ultima.consulta) {
      // "¿solo tienes esos?" / "muéstrame más": siguen los de la búsqueda anterior, sin
      // repetir los ya vistos. Antes el modelo contestaba "sí, solo esos" con el catálogo
      // lleno, porque los 8 candidatos eran lo único que conocía.
      const r = buscar(ultima.consulta, 60, contexto);
      total = r.total;
      base = r.resultados.filter(({ p }) => !ultima.mostrados.has(p.h) && !enCarro.includes(p.h)).slice(0, 8);
    } else {
      const r = buscar(pregunta, 8, contexto);
      total = r.total;
      presupuesto = r.presupuesto;
      presupuestoAsumido = r.presupuestoAsumido;
      sobrePresupuesto = r.sobrePresupuesto;
      base = r.resultados.filter(({ p }) => !enCarro.includes(p.h));
    }
    const candidatos = base.map(({ p }) => ({
      h: p.h, t: p.t, c: p.c || '', m: p.m || '', s: p.s !== false, ...(p.k ? { k: p.k } : {}),
    }));
    // Si hay una respuesta escrita a mano que calza, viaja como REFERENCIA. Sin esto la IA
    // improvisaba y se perdían los números curados (pH 5,5–6,0, 18/6 de luz) y, peor, las
    // políticas de la tienda: despacho, pago y garantía dejaban de salir como las definió
    // Pancho y pasaban a ser lo que el modelo supusiera.
    const oficial = respuestaEscrita(pregunta);
    const mensajes = [...historial, { rol: 'user', texto: pregunta.slice(0, 600) }].slice(-10);
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 25000);
    try {
      const r = await fetch(urlIa, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          mensajes, candidatos, oficial, total,
          ...(presupuesto ? { presupuesto, sobrePresupuesto, presupuestoAsumido } : {}),
          ...(carro.items.length ? { carro: carro.items.map(i => i.t).slice(0, 10) } : {}),
        }),
        signal: ctl.signal,
      });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      if (!j || typeof j.texto !== 'string' || !j.texto.trim()) throw new Error('respuesta vacía');
      const productos = (Array.isArray(j.productos) ? j.productos : [])
        .map(h => D.porHandle.get(h)).filter(Boolean).map(p => ({ p }));
      // El tope viaja con la respuesta: el pintado lo vuelve a comprobar contra el precio en
      // vivo, así el modelo no puede colar algo más caro de lo que el cliente dijo que tiene.
      return { texto: j.texto, productos, tope: presupuesto };
    } finally {
      clearTimeout(timer);
    }
  }

  // ── Precio en vivo ─────────────────────────────────────────────────────────────
  // El índice sirve para encontrar; el precio se pregunta siempre, porque cambia.
  const cachePrecio = new Map();
  async function precioDe(handle) {
    if (cachePrecio.has(handle)) return cachePrecio.get(handle);
    try {
      const r = await fetch(`/products/${handle}.js`);
      if (!r.ok) throw new Error(r.status);
      const j = await r.json();
      // `variantes`: si el producto tiene más de una (talla, sabor, medida), NO se puede
      // agregar al carrito desde el chat sin elegir cuál — se manda a la ficha. Adivinar la
      // primera es la forma más rápida de que llegue el sabor equivocado.
      const disponibles = (j.variants || []).filter(v => v.available);
      const v = {
        precio: j.price / 100, disponible: j.available, titulo: j.title, foto: j.featured_image,
        variantes: (j.variants || []).length,
        idVariante: disponibles.length === 1 ? disponibles[0].id : null,
      };
      cachePrecio.set(handle, v);
      return v;
    } catch { return null; }
  }

  // ── Qué lleva puesto el cliente ────────────────────────────────────────────────
  // Sin esto el bot recomendaba un segundo LED a alguien que ya había agregado uno. Lo que
  // está en el carrito no se vuelve a ofrecer: se ofrece lo que FALTA.
  async function carritoActual() {
    try {
      const c = await fetch('/cart.js').then(r => r.json());
      const items = (c.items || []).map(i => ({ h: i.handle, t: i.product_title || i.title }));
      const cats = new Set(items.map(i => D.porHandle.get(i.h)?.c).filter(Boolean));
      return { items, cats };
    } catch { return { items: [], cats: new Set() }; }
  }

  // Un cultivo indoor necesita estas siete cosas. El orden es el de armado real, que es como
  // lo explica Pancho: primero dónde, después la luz, y al final lo que se mide.
  const COMPLEMENTOS = ['carpas', 'iluminacion', 'sustratos', 'macetas', 'extraccion', 'nutrientes', 'medicion'];
  const pideComplementos = t =>
    /que mas (tengo que |debo )?(comprar|necesito|llevar|falta)|que me falta|que otra cosa|complement|junto con|ademas de esto|para completar/.test(limpiar(t));

  // Un producto representativo de cada categoría que falte. Ni el más barato (suele ser un
  // accesorio suelto: un sustrato de 1 litro) ni el más caro: el de precio mediano, que es el
  // que de verdad compra alguien armando su primer indoor.
  function faltantes(cats, yaEnCarro, tope = 6) {
    const fuera = new Set(yaEnCarro);
    const out = [];
    for (const cat of COMPLEMENTOS) {
      if (cats.has(cat)) continue;                       // eso ya lo lleva
      const dela = docs
        .filter(d => d.p.c === cat && d.p.n && d.p.s && d.p.p > 0 && !fuera.has(d.p.h))
        .sort((a, b) => a.p.p - b.p.p);
      if (!dela.length) continue;
      out.push({ p: dela[Math.floor(dela.length / 2)].p, s: 10 });
      if (out.length >= tope) break;
    }
    return out;
  }

  // Agrega al carrito sin salir del chat. Devuelve el total de ítems o null si falló.
  async function agregarAlCarrito(idVariante) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ items: [{ id: idVariante, quantity: 1 }] }),
    });
    if (!r.ok) return null;
    const carro = await fetch('/cart.js').then(x => x.json()).catch(() => null);
    return carro ? carro.item_count : 0;
  }

  // ── Medición ───────────────────────────────────────────────────────────────────
  // Dos destinos, cada uno para lo suyo:
  //  - GA4 (si la tienda lo tiene): estadísticas de USO — preguntas, clics, sesiones.
  //  - Atributos del carrito de Shopify: atribución de VENTAS. El atributo viaja con el
  //    carrito hasta el pedido, así que "cuánto vendió el bot" se responde consultando los
  //    pedidos de siempre — sin base de datos nueva y sin depender de retención de logs.
  function registrar(evento, datos) {
    try { if (typeof window.gtag === 'function') window.gtag('event', evento, datos || {}); } catch { /* sin GA no pasa nada */ }
  }
  // Las marcas se encadenan: dos llamadas simultáneas a /cart/update.js pueden resolverse en
  // cualquier orden y la que llegue última gana. Sin esta cola, la marca de "hizo una
  // pregunta" (que solo trae Panchobot: sí) alcanzaba a pisar la de "agregó este producto",
  // y la venta quedaba atribuida sin decir a qué producto.
  let colaCarrito = Promise.resolve();
  function marcarCarrito(atributos) {
    colaCarrito = colaCarrito.then(() =>
      // keepalive: el clic en un producto navega a otra página y sin esto el navegador
      // cancelaría la marca justo en el momento que más importa.
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ attributes: atributos }),
        keepalive: true,
      }).catch(() => {})
    ).catch(() => {});   // nunca romper el chat ni la cola por la medición
    return colaCarrito;
  }
  const sesion = {
    get usado() { try { return sessionStorage.getItem('pb-uso') === '1'; } catch { return false; } },
    marcarUso() { try { sessionStorage.setItem('pb-uso', '1'); } catch {} },
    get clics() { try { return JSON.parse(sessionStorage.getItem('pb-clics') || '[]'); } catch { return []; } },
    sumarClic(h) {
      try {
        const c = this.clics; if (!c.includes(h)) c.push(h);
        sessionStorage.setItem('pb-clics', JSON.stringify(c.slice(-20)));
        return c;
      } catch { return [h]; }
    },
  };

  // ── Interfaz ───────────────────────────────────────────────────────────────────
  function montar(raiz) {
    const url = raiz.dataset.datos;
    const wsp = raiz.dataset.whatsapp || '';
    const iaUrl = raiz.dataset.ia || '';
    const icono = raiz.dataset.icono || '';
    raiz.innerHTML = `
      <button class="pb-burbuja" aria-label="Abrir el chat con Pancho de DeGrow" aria-expanded="false">
        <span class="pb-anillo" aria-hidden="true"></span>
        ${icono
          ? `<img class="pb-icono" src="${icono}" alt="" width="34" height="34">`
          : `<svg class="pb-icono" viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
               <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.7-.8L3 20l1-4.9a8.3 8.3 0 0 1-1-4A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5Z"/>
               <path d="M12 15.5c0-2.5 0-4.5 3.5-6.5M12 15.5c0-2.5 0-4.5-3.5-6.5M12 15.5V17"/>
             </svg>`}
        <span class="pb-globito" aria-hidden="true">¿Dudas? Habla con Pancho</span>
      </button>
      <section class="pb-panel" hidden aria-live="polite">
        <header class="pb-cab">
          <strong>Pancho</strong>
          <span class="pb-sub">DeGrow</span>
          <button class="pb-nueva" aria-label="Empezar una conversación nueva" title="Conversación nueva">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 15.5-6.2M21 12a9 9 0 0 1-15.5 6.2"/><path d="M18 3v4h-4M6 21v-4h4"/>
            </svg>
          </button>
          <button class="pb-cerrar" aria-label="Cerrar">×</button>
        </header>
        <div class="pb-hilo"></div>
        <div class="pb-sugerencias"></div>
        <form class="pb-form">
          <input class="pb-input" type="text" autocomplete="off" placeholder="Escribe tu pregunta…" aria-label="Tu pregunta">
          <button class="pb-enviar" type="submit" aria-label="Enviar">→</button>
        </form>
      </section>`;

    const burbuja = $('.pb-burbuja', raiz), panel = $('.pb-panel', raiz);
    const hilo = $('.pb-hilo', raiz), form = $('.pb-form', raiz), input = $('.pb-input', raiz);
    // Se guarda el placeholder original: al cerrar la conversación se reemplaza por
    // "Conversación cerrada" y hay que poder devolverlo tal cual estaba.
    input.dataset.phOriginal = input.placeholder;
    const sugerencias = $('.pb-sugerencias', raiz);

    const escapar = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    function burbujaMsg(clase, html) {
      const div = document.createElement('div');
      div.className = `pb-msg pb-${clase}`;
      div.innerHTML = html;
      hilo.appendChild(div);
      hilo.scrollTop = hilo.scrollHeight;
      return div;
    }

    // ── Efecto de tipeo ──────────────────────────────────────────────────────────
    // El texto aparece de a poco, como si Pancho lo estuviera escribiendo. Es SOLO
    // presentación: la respuesta ya llegó entera, no se está transmitiendo por partes.
    //
    // Se escribe en un nodo de TEXTO y no en innerHTML: así ningún trozo a medio escribir
    // puede armar una etiqueta, y de paso no hay nada que escapar.
    const quiereQuieto = () =>
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    async function escribirTexto(div, texto, instantaneo = false) {
      div.textContent = '';
      const span = document.createElement('span');
      span.className = 'pb-texto';
      div.appendChild(span);
      // Al repintar el hilo guardado va instantáneo: volver de una ficha y tener que esperar
      // a que se reescriba toda la conversación sería una lata, no un detalle simpático.
      if (instantaneo || quiereQuieto()) { span.textContent = texto; return; }

      div.dataset.escribiendo = '1';
      span.classList.add('pb-cursor');
      // El total está acotado: una respuesta larga no puede tener al cliente esperando, así
      // que el trozo crece con el largo y cualquier texto termina alrededor de 1,2 segundos.
      const MS = 16;
      const porPaso = Math.max(1, Math.ceil(texto.length / (1200 / MS)));
      for (let i = 0; i < texto.length; i += porPaso) {
        span.textContent = texto.slice(0, i + porPaso);
        // Se sigue el scroll SOLO si el cliente está mirando el final. Si subió a releer algo,
        // arrastrarlo hacia abajo sería peor que no animar.
        if (hilo.scrollHeight - hilo.scrollTop - hilo.clientHeight < 60) hilo.scrollTop = hilo.scrollHeight;
        await new Promise(r => setTimeout(r, MS));
      }
      span.textContent = texto;
      span.classList.remove('pb-cursor');
      delete div.dataset.escribiendo;
    }

    // `topeP` es la última línea de defensa del presupuesto. El filtro del buscador usa el
    // precio del índice, que es una referencia; acá se compara contra el precio EN VIVO y se
    // descarta la tarjeta antes de pintarla. Así, aunque el modelo se equivoque o el índice
    // esté viejo, al cliente no le llega nada por sobre lo que dijo que podía gastar.
    async function pintarProductos(div, productos, topeP = null) {
      const lista = document.createElement('div');
      lista.className = 'pb-productos';
      div.appendChild(lista);
      let fuera = 0;
      for (const { p } of productos) {
        const vivo = await precioDe(p.h);
        if (topeP && vivo && vivo.precio > topeP) { fuera++; continue; }
        const fila = document.createElement('div');
        fila.className = 'pb-fila';

        const a = document.createElement('a');
        a.className = 'pb-producto';
        a.href = `/products/${p.h}`;
        a.innerHTML =
          (vivo?.foto ? `<img src="${escapar(vivo.foto)}&width=120" alt="" loading="lazy">` : '<span class="pb-sinfoto"></span>') +
          `<span class="pb-nombre">${escapar(vivo?.titulo || p.t)}</span>` +
          `<span class="pb-precio">${vivo ? clp(vivo.precio) : ''}${vivo && !vivo.disponible ? ' · agotado' : ''}</span>`;
        // El clic en un producto recomendado es LA señal de venta asistida: se anota en GA y
        // se deja la lista acumulada en el carrito, que después aparece en el pedido.
        a.addEventListener('click', () => {
          registrar('panchobot_click_producto', { handle: p.h });
          const clics = sesion.sumarClic(p.h);
          marcarCarrito({ 'Panchobot': 'sí', 'Panchobot productos': clics.join(', ') });
        });
        fila.appendChild(a);

        // Botón de agregar. Solo cuando NO hay ambigüedad: si el producto tiene varias
        // variantes disponibles hay que elegir cuál, y adivinar la primera es la forma más
        // rápida de despachar el sabor equivocado. En ese caso se manda a la ficha.
        if (vivo?.disponible && vivo.idVariante) {
          const b = document.createElement('button');
          b.className = 'pb-agregar';
          b.type = 'button';
          b.textContent = 'Agregar';
          b.setAttribute('aria-label', `Agregar ${vivo.titulo} al carrito`);
          b.addEventListener('click', async () => {
            b.disabled = true;
            b.textContent = '…';
            const total = await agregarAlCarrito(vivo.idVariante);
            if (total === null) { b.disabled = false; b.textContent = 'Reintentar'; return; }
            b.textContent = 'Agregado';
            b.classList.add('pb-agregado');
            // Agregar desde el chat es la señal de venta MÁS fuerte que existe: se marca igual
            // que un clic, y además se refresca el contador del carrito del tema para que el
            // cliente vea que pasó algo sin tener que recargar.
            registrar('panchobot_agregado_al_carrito', { handle: p.h });
            const clics = sesion.sumarClic(p.h);
            // Se espera: si el cliente se va a pagar de inmediato, el pedido tiene que salir
            // ya marcado. Es la diferencia entre saber que el bot vendió y no saberlo.
            await marcarCarrito({ 'Panchobot': 'sí', 'Panchobot productos': clics.join(', ') });
            document.querySelectorAll('.cart-count-bubble span:first-child').forEach(s => { s.textContent = total; });
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          });
          fila.appendChild(b);
        }
        lista.appendChild(fila);
      }
      // Si el tope dejó la lista vacía, se dice — callar se leería como "no tengo nada".
      if (topeP && !lista.childElementCount && fuera) {
        const aviso = document.createElement('div');
        aviso.className = 'pb-pie';
        aviso.textContent = `No me queda nada de eso bajo ${clp(topeP)}. Escríbenos por WhatsApp y vemos alternativas.`;
        lista.appendChild(aviso);
      }
      hilo.scrollTop = hilo.scrollHeight;
    }

    const historial = [];

    // ── Persistencia del hilo ────────────────────────────────────────────────────
    // Entrar a una ficha recomendada navega fuera de la página, y sin esto la conversación
    // moría justo cuando el cliente hacía lo que se le pidió: mirar el producto. El hilo se
    // guarda en sessionStorage (vive lo que la pestaña, igual que la marca de uso) y al
    // volver se repinta. Los PRECIOS de las tarjetas no se guardan jamás: al repintar se
    // vuelven a pedir en vivo, como siempre.
    const HILO_KEY = 'pb-hilo';
    const registro = [];
    function guardarHilo() {
      try {
        sessionStorage.setItem(HILO_KEY, JSON.stringify({
          registro: registro.slice(-16), historial, rechazos, avisosIlicito, cerrado, ultimaNegativa,
          abierto: !panel.hidden,
        }));
      } catch { /* sin storage no hay persistencia, pero el chat funciona igual */ }
    }
    function hiloGuardado() {
      try { return JSON.parse(sessionStorage.getItem(HILO_KEY)) || null; } catch { return null; }
    }
    const pieWhatsapp = () =>
      `<div class="pb-pie"><a class="pb-wsp" href="https://wa.me/${escapar(wsp)}" target="_blank" rel="noopener">Escribir por WhatsApp</a></div>`;

    // ── Corte por insistencia ────────────────────────────────────────────────────
    // Si alguien insiste con un tema que el bot no responde, repetirle la misma negativa diez
    // veces es fome y, peor, cada intento que no sea un límite duro gasta una llamada a la IA.
    // A la tercera negativa seguida se cierra la conversación y se deriva a un humano.
    //
    // Se cuentan seguidas y no totales a propósito: alguien que pregunta algo fuera de tema,
    // recibe su respuesta y después consulta bien por un producto no merece castigo.
    const MAX_RECHAZOS = 3;
    let rechazos = 0;
    // La última negativa dicha, para poder repetirla si el cliente insiste en vez de
    // tratar la insistencia como una pregunta nueva.
    let ultimaNegativa = null;
    let cerrado = false;
    // Los ilícitos van por separado y más corto: advertencia y corte a la SEGUNDA. No se
    // reinicia el contador aunque intercale preguntas normales — quien tantea con droga o
    // armas y después pregunta por una carpa sigue siendo el mismo que tanteó.
    //
    // Vive en su PROPIA llave de sessionStorage, aparte del hilo, porque el botón de
    // conversación nueva no debe limpiarlo: si bastara con apretar un botón para volver a
    // cero, el corte no serviría de nada. Se borra al cerrar la pestaña, como todo lo demás.
    const ILICITO_KEY = 'pb-ilicito';
    let avisosIlicito = (() => {
      try { return Number(sessionStorage.getItem(ILICITO_KEY)) || 0; } catch { return 0; }
    })();
    const guardarIlicito = () => {
      try { sessionStorage.setItem(ILICITO_KEY, String(avisosIlicito)); } catch { /* sin storage, igual funciona en memoria */ }
    };

    // Los núcleos NO se escriben a mano uno por uno: se sacan de los propios límites del
    // índice, así un límite nuevo cuenta como negativa sin que nadie tenga que acordarse de
    // agregarlo acá. Al agregar "eso no lo vendemos" con la lista fija, el bot dejó de
    // reconocer su propia negativa: no la repetía al insistirle y no sumaba al contador que
    // cierra la conversación.
    const NUCLEOS_FIJOS = ['de eso no manejo'];
    const nucleosRechazo = () => [
      ...NUCLEOS_FIJOS,
      ...(D.limites || []).map(l => limpiar(l.r).split(/[.,]/)[0]).filter(x => x.length > 12),
    ];
    const esRechazo = t => nucleosRechazo().some(n => limpiar(t).includes(n));

    // `conWhatsapp` se apaga en el corte por ilícitos: derivar a un humano a alguien que venía
    // preguntando por droga o armas es mandarle el problema a Pancho por WhatsApp.
    function cerrarConversacion(texto, motivo, conWhatsapp = true) {
      cerrado = true;
      input.disabled = true;
      input.placeholder = 'Conversación cerrada';
      $('.pb-enviar', raiz).disabled = true;
      const div = burbujaMsg('bot', escapar(texto));
      if (conWhatsapp && wsp) div.innerHTML += pieWhatsapp();
      registro.push({ rol: 'bot', t: texto, wsp: !!(conWhatsapp && wsp) });
      guardarHilo();
      registrar('panchobot_conversacion_cerrada', { motivo });
      hilo.scrollTop = hilo.scrollHeight;
    }

    async function preguntar(texto) {
      if (cerrado) return;
      burbujaMsg('yo', escapar(texto));
      registro.push({ rol: 'yo', q: texto });
      input.value = '';
      const pensando = burbujaMsg('bot', '<span class="pb-puntos"><i></i><i></i><i></i></span>');
      await cargar(url);

      // La primera pregunta de la sesión marca el carrito: si esa visita termina en compra,
      // el pedido llega con "Panchobot: sí" y la venta queda atribuida.
      registrar('panchobot_pregunta', { con_ia: !!iaUrl });
      if (!sesion.usado) { sesion.marcarUso(); marcarCarrito({ 'Panchobot': 'sí' }); }

      // Los ilícitos se revisan PRIMERO y nunca llegan a la IA: no se le pregunta al modelo
      // qué opina de vender droga, ni siquiera para que diga que no.
      if (esIlicito(texto)) {
        avisosIlicito++;
        guardarIlicito();
        registrar('panchobot_ilicito', { aviso: avisosIlicito });
        if (avisosIlicito >= 2) {
          pensando.remove();
          cerrarConversacion(D.ilegal.corte, 'ilicito', false);
          return;
        }
        await escribirTexto(pensando, D.ilegal.advertencia);
        registro.push({ rol: 'bot', t: D.ilegal.advertencia });
        guardarHilo();
        hilo.scrollTop = hilo.scrollHeight;
        return;
      }

      // Pedir hablar con una persona se responde ACÁ, sin gastar llamada: es una pregunta con
      // una sola respuesta correcta y el cliente que la hace ya decidió que quiere un humano.
      // Acotado a propósito: "número" o "atención" sueltos aparecen en preguntas legítimas
      // ("qué número de maceta uso"), y mandarlas a WhatsApp sería dejar de responder algo
      // que el bot sabe. Tiene que pedir el canal o pedir una persona, explícitamente.
      if (/whatsapp|\bwsp\b|hablar con (alguien|una persona|un humano|un vendedor|pancho)|atienda una persona|(su|el) (n[uú]mero|tel[eé]fono) (de |para )?(contacto|whatsapp|la tienda)?/.test(limpiar(texto))) {
        await escribirTexto(pensando, 'Dale, te dejo el WhatsApp del equipo. Ahí te atiende alguien de la tienda.');
        if (wsp) pensando.innerHTML += pieWhatsapp();
        registro.push({ rol: 'bot', t: 'Dale, te dejo el WhatsApp del equipo. Ahí te atiende alguien de la tienda.', wsp: !!wsp });
        registrar('panchobot_derivado_whatsapp', {});
        guardarHilo();
        hilo.scrollTop = hilo.scrollHeight;
        return;
      }

      // Los límites duros se resuelven ACÁ, siempre, haya IA o no: no gastan llamada y no
      // dependen de que el modelo se porte bien.
      const corte = limiteLocal(texto);
      let r;
      if (corte) {
        r = corte;
      } else if (ultimaNegativa && esInsistencia(texto)) {
        // Insistir sobre una negativa NO abre una pregunta nueva: se repite la misma negativa,
        // más firme. Ni se busca en el catálogo ni se gasta una llamada — y como el texto
        // sigue siendo una negativa, suma al contador que cierra la conversación a la tercera.
        r = { texto: `Igual que antes, bro: ${ultimaNegativa}` };
      } else if (iaUrl) {
        try {
          r = await responderIA(iaUrl, historial, texto);
        } catch {
          // La IA se cayó o tardó: el pipeline local responde igual. Peor es un chat mudo.
          r = (pideComplementos(texto) && await responderComplementos(texto))
          || responder(texto, contextoDelCliente(historial));
        }
      } else {
        r = (pideComplementos(texto) && await responderComplementos(texto))
          || responder(texto, contextoDelCliente(historial));
      }

      // Si la respuesta manda a escribir por WhatsApp, el link tiene que aparecer. Con la IA
      // encendida esto no pasaba: `derivar` solo lo ponía el pipeline local cuando no
      // encontraba nada, así que Pancho decía "escríbenos por WhatsApp" y no había nada que
      // tocar. Mandar a alguien a un canal sin darle el link es perderlo en el último paso.
      if (/whatsapp|wsp\b|escr[ií]benos/i.test(r.texto)) r.derivar = true;

      // Se cuenta ANTES de pintar: si esta es la tercera negativa seguida, la respuesta se
      // muestra igual y el cierre viene detrás, para que el cliente entienda por qué se acabó.
      const negativa = esRechazo(r.texto);
      rechazos = negativa ? rechazos + 1 : 0;
      // Se recuerda CUÁL fue la negativa, para poder repetirla si el cliente insiste. Se
      // guarda sin el prefijo para no encadenar "Igual que antes: Igual que antes: ...".
      ultimaNegativa = negativa ? r.texto.replace(/^Igual que antes, bro: /, '') : null;

      historial.push({ rol: 'user', texto: texto.slice(0, 600) }, { rol: 'assistant', texto: r.texto.slice(0, 600) });
      while (historial.length > 8) historial.shift();

      await escribirTexto(pensando, r.texto);
      if (r.pie) pensando.innerHTML += `<div class="pb-pie">${escapar(r.pie)}</div>`;
      // `pintando` mientras se arman las tarjetas: cada una pide su precio en vivo y llegan de
      // a una, así que hay un rato en que el mensaje está a medio pintar.
      if (r.productos?.length) {
        pensando.dataset.pintando = '1';
        await pintarProductos(pensando, r.productos, r.tope || null);
        delete pensando.dataset.pintando;
      }
      // Lo mostrado se recuerda para poder seguir la lista si pide más, y para no repetirlo.
      if (r.productos?.length) recordarMostrados(pideMas(texto) ? '' : texto, r.productos);
      // El pie va DESPUÉS de las tarjetas: cierra la lista diciendo cuántos más hay.
      if (r.pieFinal) pensando.innerHTML += `<div class="pb-pie">${escapar(r.pieFinal)}</div>`;
      if (r.derivar && wsp) pensando.innerHTML += pieWhatsapp();
      registro.push({
        rol: 'bot', t: r.texto, pie: r.pie || '', pieFinal: r.pieFinal || '', wsp: !!(r.derivar && wsp), tope: r.tope || null,
        prods: (r.productos || []).map(({ p }) => ({ h: p.h, t: p.t })),
      });
      guardarHilo();
      if (rechazos >= MAX_RECHAZOS) {
        cerrarConversacion(
          'Ya te dije que de eso no manejo, bro. Mejor hablemos de lo que sí: escríbenos por WhatsApp y te atiende alguien del equipo.',
          'insistencia');
      }
      hilo.scrollTop = hilo.scrollHeight;
    }

    const CHIPS = ['¿Qué necesito para empezar?', '¿Qué LED para una carpa de 1x1?', '¿Cada cuánto riego?', '¿Qué pH en coco?'];

    // Saludo al azar: abrir el chat dos veces y recibir la misma frase exacta delata al robot
    // antes de que alcance a responder nada.
    function saludar() {
      const saludos = D.textos.saludos || [D.textos.saludo];
      burbujaMsg('bot', escapar(saludos[Math.floor(Math.random() * saludos.length)]));
      sugerencias.innerHTML = '';
      for (const c of CHIPS) {
        const b = document.createElement('button');
        b.className = 'pb-chip'; b.type = 'button'; b.textContent = c;
        b.addEventListener('click', () => { sugerencias.innerHTML = ''; preguntar(c); });
        sugerencias.appendChild(b);
      }
    }

    // Empezar de nuevo. El hilo persiste toda la pestaña, así que sin esto un cliente que ya
    // conversó no tenía forma de partir limpio salvo cerrar el navegador — y arrastrar
    // contexto viejo hace que el bot responda a una pregunta que ya no es la de ahora.
    //
    // Lo que NO se reinicia: el contador de ilícitos. Si bastara con apretar un botón para
    // volver a cero, el corte sería decorativo. El cierre por insistencia sí se levanta: ese
    // existe para terminar un loop fome, no para castigar a nadie.
    function nuevaConversacion() {
      if (avisosIlicito >= 2) return;             // cerrado por ilícitos: no se reabre
      hilo.innerHTML = '';
      historial.length = 0;
      registro.length = 0;
      ultima.consulta = '';
      ultima.mostrados.clear();
      rechazos = 0;
      ultimaNegativa = null;
      cerrado = false;
      input.disabled = false;
      input.value = '';
      input.placeholder = input.dataset.phOriginal || 'Escribe tu pregunta';
      $('.pb-enviar', raiz).disabled = false;
      try { sessionStorage.removeItem(HILO_KEY); } catch { /* sin storage no hay nada que borrar */ }
      saludar();
      guardarHilo();
      registrar('panchobot_conversacion_nueva', {});
      input.focus();
    }

    let abierto = false;
    async function abrir(auto = false) {
      abierto = true;
      panel.hidden = false;
      burbuja.setAttribute('aria-expanded', 'true');
      if (!hilo.childElementCount) {
        await cargar(url);
        const previo = hiloGuardado();
        if (previo?.registro?.length) {
          // El cliente vuelve de mirar una ficha: se repinta la conversación tal como quedó,
          // con contadores de corte incluidos. Cerrar la pestaña es lo único que la borra.
          historial.push(...(previo.historial || []).slice(-8));
          rechazos = previo.rechazos || 0;
          ultimaNegativa = previo.ultimaNegativa || null;
          avisosIlicito = previo.avisosIlicito || 0;
          registro.push(...previo.registro);
          for (const e of previo.registro) {
            if (e.rol === 'yo') { burbujaMsg('yo', escapar(e.q)); continue; }
            const div = burbujaMsg('bot', '');
            await escribirTexto(div, e.t, true);
            if (e.pie) div.innerHTML += `<div class="pb-pie">${escapar(e.pie)}</div>`;
            if (e.prods?.length) await pintarProductos(div, e.prods.map(p => ({ p })), e.tope || null);
            if (e.pieFinal) div.innerHTML += `<div class="pb-pie">${escapar(e.pieFinal)}</div>`;
            if (e.wsp && wsp) div.innerHTML += pieWhatsapp();
          }
          if (previo.cerrado) {
            cerrado = true;
            input.disabled = true;
            input.placeholder = 'Conversación cerrada';
            $('.pb-enviar', raiz).disabled = true;
          }
        } else {
          saludar();
        }
      }
      guardarHilo();
      // En la reapertura automática no se roba el foco: en un teléfono levantaría el teclado
      // encima de la ficha que el cliente vino a mirar.
      if (!auto) input.focus();
    }
    function cerrar() { abierto = false; panel.hidden = true; burbuja.setAttribute('aria-expanded', 'false'); guardarHilo(); }

    burbuja.addEventListener('click', () => {
      if (!abierto) registrar('panchobot_abierto', {});
      abierto ? cerrar() : abrir();
    });
    $('.pb-cerrar', raiz).addEventListener('click', cerrar);
    $('.pb-nueva', raiz).addEventListener('click', nuevaConversacion);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && abierto) cerrar(); });
    form.addEventListener('submit', e => {
      e.preventDefault();
      const v = input.value.trim();
      if (!v) return;
      sugerencias.innerHTML = '';
      preguntar(v);
    });

    // Si el cliente navegó con el panel abierto (entró a una ficha recomendada, por ejemplo),
    // se reabre solo con el hilo repintado. Si lo tenía cerrado, se queda cerrado.
    if (hiloGuardado()?.abierto) abrir(true);
  }

  const iniciar = () => document.querySelectorAll('[data-panchobot]').forEach(montar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
})();
